import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  listInvoices,
  type InvoiceSummaryDto,
  type ListInvoicesParams,
} from "@/lib/invoices-payments";
import { InvoiceStatusBadge, type InvoiceStatusCode } from "@/components/invoices-payments";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { IntentButton } from "@/components/intent-button";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// ---------------------------------------------------------------------------
// Saved-view chips (preset filter shortcuts; tie directly to filter state)
// ---------------------------------------------------------------------------

type SavedViewId = "all" | "open" | "overdue" | "paidThisWeek" | "issuedToday";

interface FilterState {
  statuses: InvoiceStatusCode[];
  client: ClientPickerValue | null;
  accountId: string;
  from: string;
  to: string;
  dueFrom: string;
  dueTo: string;
  minTotal: string;
  maxTotal: string;
  q: string;
}

const EMPTY_FILTERS: FilterState = {
  statuses: [],
  client: null,
  accountId: "",
  from: "",
  to: "",
  dueFrom: "",
  dueTo: "",
  minTotal: "",
  maxTotal: "",
  q: "",
};

const ALL_STATUSES: InvoiceStatusCode[] = [
  "Draft",
  "Sent",
  "PartPaid",
  "Paid",
  "OverPaid",
  "Overdue",
  "Cancelled",
];

const SAVED_VIEWS: { id: SavedViewId; label: string }[] = [
  { id: "all",           label: "All" },
  { id: "open",          label: "Open invoices" },
  { id: "overdue",       label: "Overdue" },
  { id: "paidThisWeek",  label: "Paid this week" },
  { id: "issuedToday",   label: "Issued today" },
];

function applySavedView(view: SavedViewId): FilterState {
  switch (view) {
    case "open":
      return { ...EMPTY_FILTERS, statuses: ["Sent", "PartPaid"] };
    case "overdue":
      return { ...EMPTY_FILTERS, statuses: ["Overdue"] };
    case "paidThisWeek":
      // Spec acknowledges Plan I server doesn't filter by paid-on; status-only.
      return { ...EMPTY_FILTERS, statuses: ["Paid"] };
    case "issuedToday": {
      const startOfDayUtc = new Date();
      startOfDayUtc.setUTCHours(0, 0, 0, 0);
      return { ...EMPTY_FILTERS, from: startOfDayUtc.toISOString() };
    }
    case "all":
    default:
      return EMPTY_FILTERS;
  }
}

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

function daysOverdue(dueDate: string | null, status: string): number | null {
  if (!dueDate) return null;
  if (status === "Paid" || status === "OverPaid" || status === "Cancelled") return null;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return null;
  const now = new Date();
  const diffMs = now.getTime() - due.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return days > 0 ? days : null;
}

// ---------------------------------------------------------------------------
// Build params from filter state
// ---------------------------------------------------------------------------

function buildParams(state: FilterState, page: number): ListInvoicesParams {
  return {
    status: state.statuses.length > 0 ? state.statuses : undefined,
    clientId: state.client?.clientId,
    accountId: state.accountId ? Number(state.accountId) : undefined,
    from: state.from || undefined,
    to: state.to || undefined,
    dueFrom: state.dueFrom || undefined,
    dueTo: state.dueTo || undefined,
    minTotal: state.minTotal ? Number(state.minTotal) : undefined,
    maxTotal: state.maxTotal ? Number(state.maxTotal) : undefined,
    q: state.q || undefined,
    page,
    pageSize: 50,
  };
}

// ---------------------------------------------------------------------------
// InvoicesListPage
// ---------------------------------------------------------------------------

export function InvoicesListPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [activeView, setActiveView] = useState<SavedViewId>("all");
  const [page, setPage] = useState(0);

  const params = useMemo(() => buildParams(filters, page), [filters, page]);

  const { data, isLoading } = useQuery({
    queryKey: ["invoices", params],
    queryFn: () => listInvoices(params),
  });

  const visibleRows = useMemo(() => {
    return data?.items ?? [];
  }, [data]);

  function applyView(id: SavedViewId) {
    setActiveView(id);
    setFilters(applySavedView(id));
    setPage(0);
  }

  function toggleStatus(s: InvoiceStatusCode) {
    setActiveView("all");
    setPage(0);
    setFilters((f) => ({
      ...f,
      statuses: f.statuses.includes(s)
        ? f.statuses.filter((x) => x !== s)
        : [...f.statuses, s],
    }));
  }

  // TODO(Plan II/III): Bulk actions deferred — CSV export and "Issue selected"
  // require a checkbox-column DataGrid extension and a multi-issue endpoint.
  // Spec lines 4011–4017 list both as Plan I items but they're moved out of
  // scope for this task per the executor brief.

  const cols: DataGridColumn<InvoiceSummaryDto>[] = [
    {
      id: "ref",
      header: "Invoice #",
      accessor: (r) => (
        <Link to={`/invoices/${r.invoiceId}`} className="underline text-primary">
          {r.invoiceReferenceNo}
        </Link>
      ),
    },
    {
      id: "issued",
      header: "Issued",
      accessor: (r) => r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString("en-AU") : "—",
    },
    {
      id: "due",
      header: "Due",
      accessor: (r) => r.dueDate ? new Date(r.dueDate).toLocaleDateString("en-AU") : "—",
    },
    // Adapt: backend returns null for ClientFullName / AccountReferenceNo on
    // the Summary DTO (Task 21 left these unpopulated). Show em-dash.
    { id: "client",  header: "Client",  accessor: (r) => r.clientFullName ?? "—" },
    { id: "account", header: "Account", accessor: (r) => r.accountReferenceNo ?? "—" },
    {
      id: "total",
      header: "Total",
      accessor: (r) => fmt.format(r.invoiceTotalIncGst),
      align: "right",
    },
    {
      id: "balance",
      header: "Balance",
      accessor: (r) => fmt.format(r.balanceOutstanding),
      align: "right",
    },
    {
      id: "status",
      header: "Status",
      accessor: (r) => <InvoiceStatusBadge code={r.invoiceStatusCode as InvoiceStatusCode} />,
    },
    {
      id: "daysOverdue",
      header: "Days overdue",
      accessor: (r) => {
        const d = daysOverdue(r.dueDate, r.invoiceStatusCode);
        return d == null ? "—" : String(d);
      },
      align: "right",
    },
    {
      id: "myob",
      header: "MYOB",
      accessor: (r) => (r.myobExported ? "✓" : "—"),
      align: "center",
    },
  ];

  return (
    <ListPage
      title="Invoices"
      primaryAction={
        <IntentButton onClick={() => navigate("/invoices/new")}>
          + New Invoice
        </IntentButton>
      }
      filterBar={
        <div className="space-y-3">
          {/* Saved-view chips */}
          <div className="flex flex-wrap gap-2">
            {SAVED_VIEWS.map((v) => (
              <Button
                key={v.id}
                variant={activeView === v.id ? "default" : "outline"}
                size="sm"
                onClick={() => applyView(v.id)}
              >
                {v.label}
              </Button>
            ))}
          </div>

          {/* Status pills (multi-select) */}
          <div className="flex flex-wrap gap-1">
            <span className="self-center text-xs text-muted-foreground mr-1">Status:</span>
            {ALL_STATUSES.map((s) => {
              const active = filters.statuses.includes(s);
              return (
                <Button
                  key={s}
                  type="button"
                  variant={active ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleStatus(s)}
                >
                  {s}
                </Button>
              );
            })}
          </div>

          {/* Filter inputs */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <div>
              <Label htmlFor="inv-q">Search ref #</Label>
              <Input
                id="inv-q"
                value={filters.q}
                onChange={(e) => { setActiveView("all"); setFilters({ ...filters, q: e.target.value }); setPage(0); }}
              />
            </div>
            <div>
              <Label>Client</Label>
              <ClientPicker
                value={filters.client}
                onChange={(v) => { setActiveView("all"); setFilters({ ...filters, client: v }); setPage(0); }}
              />
            </div>
            <div>
              <Label htmlFor="inv-account">Account ID</Label>
              <Input
                id="inv-account"
                type="number"
                value={filters.accountId}
                onChange={(e) => { setActiveView("all"); setFilters({ ...filters, accountId: e.target.value }); setPage(0); }}
              />
            </div>
            <div>
              <Label htmlFor="inv-from">Issued from</Label>
              <Input
                id="inv-from"
                type="date"
                value={filters.from ? filters.from.slice(0, 10) : ""}
                onChange={(e) => { setActiveView("all"); setFilters({ ...filters, from: e.target.value }); setPage(0); }}
              />
            </div>
            <div>
              <Label htmlFor="inv-to">Issued to</Label>
              <Input
                id="inv-to"
                type="date"
                value={filters.to ? filters.to.slice(0, 10) : ""}
                onChange={(e) => { setActiveView("all"); setFilters({ ...filters, to: e.target.value }); setPage(0); }}
              />
            </div>
            <div>
              <Label htmlFor="inv-due-from">Due from</Label>
              <Input
                id="inv-due-from"
                type="date"
                value={filters.dueFrom}
                onChange={(e) => { setActiveView("all"); setFilters({ ...filters, dueFrom: e.target.value }); setPage(0); }}
              />
            </div>
            <div>
              <Label htmlFor="inv-due-to">Due to</Label>
              <Input
                id="inv-due-to"
                type="date"
                value={filters.dueTo}
                onChange={(e) => { setActiveView("all"); setFilters({ ...filters, dueTo: e.target.value }); setPage(0); }}
              />
            </div>
            <div>
              <Label htmlFor="inv-min">Min total</Label>
              <Input
                id="inv-min"
                type="number"
                value={filters.minTotal}
                onChange={(e) => { setActiveView("all"); setFilters({ ...filters, minTotal: e.target.value }); setPage(0); }}
              />
            </div>
            <div>
              <Label htmlFor="inv-max">Max total</Label>
              <Input
                id="inv-max"
                type="number"
                value={filters.maxTotal}
                onChange={(e) => { setActiveView("all"); setFilters({ ...filters, maxTotal: e.target.value }); setPage(0); }}
              />
            </div>
          </div>
        </div>
      }
    >
      <DataGrid
        rows={visibleRows}
        columns={cols}
        rowKey={(r) => r.invoiceId}
        totalRows={data?.totalCount ?? 0}
        pageIndex={page}
        pageSize={50}
        loading={isLoading}
        emptyHeadline="No invoices match these filters."
        onPageChange={setPage}
      />
    </ListPage>
  );
}
